import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { CheckCircle2, ClipboardCheck, Loader2, BedDouble, DollarSign, Clock, FileText, Landmark, CreditCard } from "lucide-react";
import PixInfoCard from "../PixInfoCard";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

/**
 * ArrivalFlow — componente único que serve os 3 personas:
 *   role="clinica"   → mostra form quando não há arrival, ou card readonly após.
 *   role="consultor" → mostra card + botão "Confirmar chegada" se arrival && !confirmed.
 *   role="familia"   → mostra card readonly do que existe (registrado / confirmado).
 */
export const ArrivalFlow = ({ caseObj, role = "familia", onDone }) => {
  const arrival = caseObj?.arrival;
  const [form, setForm] = useState({
    admitted_at: new Date().toISOString().slice(0, 16),
    received_by: "", bed: "",
    matricula_value: "", monthly_value: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submitClinicArrival = async (e) => {
    e.preventDefault();
    if (!form.received_by.trim()) { toast.error("Informe quem recebeu o paciente."); return; }
    if (!form.matricula_value || !form.monthly_value) { toast.error("Preencha os valores."); return; }
    setBusy(true);
    try {
      await api.post(`/clinic/cases/${caseObj.id}/arrival`, {
        admitted_at: new Date(form.admitted_at).toISOString(),
        received_by: form.received_by.trim(),
        bed: form.bed.trim() || null,
        matricula_value: Number(form.matricula_value),
        monthly_value: Number(form.monthly_value),
        notes: form.notes.trim() || null,
      });
      toast.success("Chegada registrada — aguardando o consultor confirmar.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar chegada");
    } finally { setBusy(false); }
  };

  const confirmArrival = async () => {
    if (!window.confirm("Confirmar a chegada do paciente? Isso encerra o caso como INTERNADO.")) return;
    setBusy(true);
    try {
      await api.post(`/consultant/cases/${caseObj.id}/arrival/confirm`);
      toast.success("Chegada confirmada · caso encerrado como internado");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar");
    } finally { setBusy(false); }
  };

  // === Já confirmado — mostra recibo detalhado para os 3 personas ===
  if (arrival?.confirmed) {
    // Recibos usam cookie httpOnly (raf_session) — abrir em nova aba envia
    // credenciais automaticamente. Botão de repasse fica oculto para família:
    // a existência do repasse comercial clínica→RAF não é do conhecimento dela.
    const openFamilyReceipt = () => window.open(
      `${API_BASE}/api/cases/${caseObj.id}/arrival-receipt-family.pdf`,
      "_blank", "noopener",
    );
    const openInternalReceipt = () => window.open(
      `${API_BASE}/api/cases/${caseObj.id}/arrival-receipt-internal.pdf`,
      "_blank", "noopener",
    );
    return (
      <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="arrival-confirmed-card">
        <div className="flex items-center gap-2 text-emerald-800 font-display font-bold text-lg">
          <CheckCircle2 className="w-5 h-5" /> Internação confirmada
        </div>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 mt-3 text-sm">
          <Row label="Admissão" value={fmtDT(arrival.admitted_at)} />
          <Row label="Recebido por" value={arrival.received_by} />
          <Row label="Leito" value={arrival.bed || "—"} />
          <Row label="Registrado pela unidade" value={`${arrival.registered_by_name} · ${fmtDT(arrival.registered_at)}`} />
          <Row label="Confirmado pelo consultor" value={`${arrival.confirmed_by_name} · ${fmtDT(arrival.confirmed_at)}`} />
          {(role === "admin" || role === "consultor" || role === "clinica") && (
            <>
              <Row
                label={role === "clinica" ? "Matrícula (comissão RAF)" : "Matrícula (comissão RAF)"}
                value={brl(arrival.matricula_value)}
                strong
              />
              {/* Mensalidade e comissão do consultor só aparecem para admin e
                  consultor. A clínica não precisa ver esses números — mensalidade
                  é acordada direto com a família e a comissão do consultor é
                  atributo interno da RAF (10% da matrícula RAF). */}
              {role !== "clinica" && (
                <>
                  <Row label="Mensalidade" value={brl(arrival.monthly_value)} />
                  <Row label="Comissão do consultor (10% da matrícula)" value={brl(arrival.consultor_commission)} strong />
                </>
              )}
            </>
          )}
          {arrival.notes && <Row label="Observações" value={arrival.notes} full />}
        </div>

        {/* Recibos (PDF) */}
        <div className="mt-5 pt-4 border-t border-emerald-200 flex flex-wrap gap-2">
          <button
            onClick={openFamilyReceipt}
            data-testid="download-arrival-receipt-family"
            className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-700 text-emerald-800 rounded-md text-xs font-semibold hover:bg-emerald-800 hover:text-white transition-colors"
            title="Recibo de matrícula emitido pela unidade — pode ser entregue à família"
          >
            <FileText className="w-3.5 h-3.5" />
            {role === "familia" ? "Baixar meu recibo (PDF)" : "Recibo da família (PDF)"}
          </button>
          {role !== "familia" && (
            <button
              onClick={openInternalReceipt}
              data-testid="download-arrival-receipt-internal"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[#0B2B62] text-[#0B2B62] rounded-md text-xs font-semibold hover:bg-[#0B2B62] hover:text-white transition-colors"
              title="Recibo de repasse Clínica→RAF com comissão do consultor discriminada — uso interno"
            >
              <Landmark className="w-3.5 h-3.5" />
              Recibo de repasse RAF (PDF)
            </button>
          )}
        </div>

        {/* Repasse via Stripe — só clínica vê. Iter 68.10 */}
        {role === "clinica" && (
          <RepasseCheckoutBlock caseObj={caseObj} onDone={onDone} />
        )}
      </section>
    );
  }

  // === Registrado mas AGUARDANDO CONFIRMAÇÃO ===
  if (arrival && !arrival.confirmed) {
    return (
      <section className="bg-amber-50 border border-amber-300 rounded-xl p-5 space-y-3" data-testid="arrival-pending-card">
        <div className="flex items-center gap-2 text-amber-800 font-display font-bold text-lg">
          <Clock className="w-5 h-5" /> Aguardando confirmação do consultor
        </div>
        <p className="text-sm text-amber-900">
          A unidade <b>{caseObj.clinic_name}</b> registrou a chegada. O consultor precisa
          confirmar para encerrar o caso como <b>internação realizada</b>.
        </p>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-800 mt-2">
          <Row label="Admissão" value={fmtDT(arrival.admitted_at)} />
          <Row label="Recebido por" value={arrival.received_by} />
          <Row label="Leito" value={arrival.bed || "—"} />
          <Row label="Registrado por" value={`${arrival.registered_by_name} · ${fmtDT(arrival.registered_at)}`} />
          {role !== "familia" && (
            <>
              <Row label="Matrícula (RAF)" value={brl(arrival.matricula_value)} />
              {role !== "clinica" && (
                <Row label="Mensalidade" value={brl(arrival.monthly_value)} />
              )}
            </>
          )}
          {arrival.notes && <Row label="Observações" value={arrival.notes} full />}
        </div>
        {(role === "consultor" || role === "admin") && (
          <button onClick={confirmArrival} disabled={busy} data-testid="arrival-confirm-btn"
            className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white rounded-md font-semibold text-sm hover:bg-emerald-800 disabled:opacity-50">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Confirmando…</> : <><CheckCircle2 className="w-4 h-4" /> Confirmar chegada e encerrar</>}
          </button>
        )}
      </section>
    );
  }

  // === Nada registrado ainda ===
  if (role === "clinica") {
    return (
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="arrival-form-card">
        <div className="flex items-center gap-2 text-[#0B2B62] font-display font-bold text-lg">
          <ClipboardCheck className="w-5 h-5" /> Registrar chegada do paciente
        </div>
        <p className="text-sm text-slate-600">
          Preencha assim que o paciente for admitido. Depois o consultor RAF confirma
          e o caso é encerrado como <b>internação realizada</b>. A comissão do consultor é
          calculada automaticamente (<b>10% da matrícula RAF</b>). A mensalidade é acordada
          direto com a família e não gera comissão.
        </p>
        <form onSubmit={submitClinicArrival} className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Data/hora da admissão *">
              <input type="datetime-local" required data-testid="arrival-admitted-at"
                value={form.admitted_at} onChange={(e) => setForm({ ...form, admitted_at: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </Field>
            <Field label="Quem recebeu o paciente *">
              <input type="text" required data-testid="arrival-received-by" placeholder="Nome do recepcionista/enfermeiro"
                value={form.received_by} onChange={(e) => setForm({ ...form, received_by: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </Field>
            <Field label="Leito atribuído">
              <input type="text" data-testid="arrival-bed" placeholder="Ex: 12A"
                value={form.bed} onChange={(e) => setForm({ ...form, bed: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </Field>
            <Field label="Matrícula · comissão RAF (R$) *" hint={form.matricula_value ? `10% consultor = ${brl(Number(form.matricula_value) * 0.10)}` : null}>
              <div className="relative">
                <DollarSign className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <input type="number" required min="0" step="0.01" data-testid="arrival-matricula"
                  value={form.matricula_value} onChange={(e) => setForm({ ...form, matricula_value: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
            </Field>
            <Field label="Mensalidade total (R$) *" hint="Acordada direto com a família — informativo, não gera comissão.">
              <div className="relative">
                <DollarSign className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
                <input type="number" required min="0" step="0.01" data-testid="arrival-monthly"
                  value={form.monthly_value} onChange={(e) => setForm({ ...form, monthly_value: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-md text-sm" />
              </div>
            </Field>
            <Field label="Observações">
              <textarea rows={2} data-testid="arrival-notes"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </Field>
          </div>
          <button type="submit" disabled={busy} data-testid="arrival-submit-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0B2B62] text-white rounded-md font-semibold text-sm hover:bg-slate-800 disabled:opacity-50">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Registrando…</> : <><BedDouble className="w-4 h-4" /> Registrar chegada</>}
          </button>
        </form>
      </section>
    );
  }

  // Consultor/família aguardando a clínica preencher
  if (role === "consultor") {
    return (
      <section className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-4 text-sm text-slate-600" data-testid="arrival-waiting-clinic">
        Aguardando a unidade parceira registrar a chegada. Quando isso acontecer, você recebe uma notificação para confirmar.
      </section>
    );
  }
  return null;
};

const Row = ({ label, value, strong, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className={`text-slate-800 ${strong ? "font-bold text-[#0B2B62]" : ""}`}>{value}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-emerald-700 mt-1">{hint}</div>}
  </label>
);


/**
 * RepasseCheckoutBlock — botão pra clínica pagar o repasse da matrícula
 * à RAF via cartão (ou PIX quando conta Stripe ativar). Aparece após o
 * consultor confirmar a chegada. Idempotente: se já pago, mostra confirmação.
 */
const RepasseCheckoutBlock = ({ caseObj }) => {
  const repasse = caseObj?.repasse;
  const matricula = Number(caseObj?.arrival?.matricula_value || 0);
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/clinic/cases/${caseObj.id}/repasse/checkout`, {
        origin_url: window.location.origin,
      });
      if (r?.data?.checkout_url) window.location.href = r.data.checkout_url;
      else toast.error("Não foi possível iniciar o pagamento");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar pagamento");
    } finally { setBusy(false); }
  };

  if (repasse?.status === "paid") {
    return (
      <div className="mt-4 p-3 rounded-md bg-white border border-emerald-300 text-sm text-emerald-900 flex items-start gap-2"
           data-testid="repasse-paid-card">
        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <b>Repasse da matrícula pago à RAF</b> · {brl(repasse.amount)} · {fmtDT(repasse.paid_at)}
          <div className="text-[11px] text-emerald-700 mt-0.5">Comprovante disponível no seu painel Stripe.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 rounded-md bg-white border-2 border-dashed border-[#0B2B62] flex items-start justify-between flex-wrap gap-3"
         data-testid="repasse-checkout-card">
      <div className="flex-1 min-w-[220px]">
        <div className="text-xs uppercase tracking-widest text-[#0B2B62] font-black flex items-center gap-1.5">
          <Landmark className="w-3.5 h-3.5" /> Repasse da matrícula à RAF
        </div>
        <div className="mt-1 text-2xl font-display font-bold text-[#0B2B62]">
          {brl(matricula)}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {repasse?.status === "pending"
            ? "Link já gerado — clique novamente pra reabrir o checkout."
            : "Pague o repasse comercial online. Cartão de crédito · PIX (ativado)."}
        </div>
      </div>
      <button onClick={pay} disabled={busy || matricula <= 0}
              data-testid="repasse-pay-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0B2B62] text-white rounded-md font-semibold text-sm hover:bg-[#08204a] disabled:opacity-50 transition-colors">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Abrindo…</>
              : <><CreditCard className="w-4 h-4" /> Pagar repasse</>}
      </button>
      <div className="w-full mt-3"><PixInfoCard compact /></div>
    </div>
  );
};

export default ArrivalFlow;
